// release-panel-selection.ts — PSTUDIO-4415

/**
 * The Expanded Cell View panel and its JSON editor host. The same selector
 * `cell.tsx`'s `handleBlur` uses to recognise "focus is entering the panel".
 */
const PANEL_SELECTOR = '[class*="c-expanded-cell-view"], ps-code-editor';

/**
 * Drop a document selection that is still sitting inside the Expanded Cell
 * View panel. Returns whether it did.
 *
 * Called when a grid cell takes focus. Measured live (S55): with the panel
 * open, a click on a grid cell moves FOCUS to the <td> but leaves the document
 * SELECTION in the panel's JSON editor, and Chrome delivers the next Cmd+V to
 * that selection — CodeMirror inserted the text, `defaultPrevented` was set by
 * it, the grid's capture listener saw the editor as the target and stood down,
 * and the panel's commit later stored the paste laid out over several lines
 * instead of the compact value the grid paste writes at once.
 *
 * Only a selection anchored in the panel is touched (the walk hops out of
 * shadow roots, because the editor's text lives in ps-code-editor's), so a
 * selection the user made anywhere else — or inside the cell itself — stays.
 * Focus is not moved: `removeAllRanges` changes the selection only.
 */
export function releasePanelSelection(doc: Document = document): boolean {
  const selection = doc.getSelection?.();
  if (!selection || selection.rangeCount === 0) return false;
  if (!isInsidePanel(selection.anchorNode)) return false;
  selection.removeAllRanges();
  return true;
}

const MAX_HOPS = 1000;
const ELEMENT_NODE = 1;
const DOCUMENT_FRAGMENT_NODE = 11;

function isInsidePanel(node: Node | null): boolean {
  let current: Node | null = node;
  let hops = 0;
  while (current && hops++ < MAX_HOPS) {
    if (
      current.nodeType === ELEMENT_NODE &&
      (current as Element).matches(PANEL_SELECTOR)
    ) {
      return true;
    }
    // Hop to the host only at a real shadow root (same reasoning as
    // `composedContains`: an anchor's `host` is a string).
    const { host } = current as { host?: unknown };
    current =
      current.nodeType === DOCUMENT_FRAGMENT_NODE &&
      !!host &&
      typeof host === 'object' &&
      'nodeType' in (host as object)
        ? (host as Node)
        : current.parentNode;
  }
  return false;
}
